import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import NoticeModal from '../NoticeModal';
import HelpModal from '../HelpModal';
import { 
  ChartBarIcon, 
  BellAlertIcon, 
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  CheckCircleIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  UserIcon,
  BanknotesIcon,
  PlusIcon,
  ChevronRightIcon,
  TrophyIcon,
  SparklesIcon,
  ChatBubbleLeftRightIcon,
  SpeakerWaveIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';

const AdminDashboard = () => {
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [selectedHelp, setSelectedHelp] = useState(null);
  const navigate = useNavigate();

  const handleActivityClick = (activity) => {
    // 활동 타입과 액션에 따라 적절한 페이지로 이동
    if (activity.project_id) {
      // 메시지 전송 활동인 경우 채팅 탭으로 이동
      if (activity.action === 'message_sent') {
        navigate(`/projects/${activity.project_id}?tab=chat`);
      } else {
        // 그 외의 경우 프로젝트 상세 페이지로 이동
        navigate(`/projects/${activity.project_id}`);
      }
    } else if (activity.user_id && activity.type === 'contractor') {
      // 소장 프로필 페이지로 이동
      navigate(`/admin/contractors/${activity.user_id}`);
    }
  };
  // 오늘의 현황 통계
  const { data: stats } = useQuery(
    'adminStats',
    () => api.get('/admin/dashboard/stats').then(res => res.data),
    { refetchInterval: 30000 }
  );



  // 월별 통계 차트 데이터
  const { data: chartData } = useQuery(
    'monthlyStats',
    () => api.get('/admin/monthly-stats').then(res => res.data)
  );

  // 최근 공지사항
  const { data: recentNotices } = useQuery(
    'recentNotices',
    () => api.get('/notices?limit=3').then(res => res.data?.notices || [])
  );

  // 최근 도움말
  const { data: recentHelps } = useQuery(
    'recentHelps',
    () => api.get('/helps?limit=3').then(res => res.data?.helps || [])
  );

  // 미확인 프로젝트 (소장이 확인하지 않은)
  const { data: unreadProjects } = useQuery(
    'unreadProjects',
    () => api.get('/admin/projects/unread').then(res => res.data),
    { refetchInterval: 60000 }
  );

  // VIP 인출 대기 현황
  const { data: vipWithdrawalStats } = useQuery(
    'vipWithdrawalStats',
    () => api.get('/admin/vip/withdrawal-stats').then(res => res.data),
    { refetchInterval: 30000 }
  );

  return (
    <div className="space-y-6 pb-20">
      {/* 상단 헤더 - 개선된 디자인 */}
      <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-2xl p-6 text-white overflow-hidden">
        {/* 배경 패턴 */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-white rounded-full"></div>
          <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-white rounded-full"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white rounded-full"></div>
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold mb-1">관리자님 🎉</h1>
              <p className="text-blue-100 text-lg">{format(new Date(), 'yyyy년 M월 d일 EEEE', { locale: ko })}</p>
            </div>
            <Link 
              to="/admin/projects/create" 
              className="group flex items-center justify-center bg-white/20 backdrop-blur p-3 rounded-xl hover:bg-white/30 transition-all hover:scale-105"
              title="새 프로젝트"
            >
              <PlusIcon className="h-6 w-6" />
            </Link>
          </div>
          
          {/* 빠른 통계 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
              <p className="text-blue-100 text-sm mb-1">오늘 매출</p>
              <p className="text-2xl font-bold">
                {stats?.todayRevenue ? `${(stats.todayRevenue / 10000).toLocaleString()}만원` : '0원'}
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
              <p className="text-blue-100 text-sm mb-1">주간 매출</p>
              <p className="text-2xl font-bold">
                {stats?.weeklyRevenue ? `${(stats.weeklyRevenue / 10000).toLocaleString()}만원` : '0원'}
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
              <p className="text-blue-100 text-sm mb-1">월간 매출</p>
              <p className="text-2xl font-bold">
                {stats?.monthlyRevenue ? `${(stats.monthlyRevenue / 100000000).toFixed(1)}억` : '0원'}
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
              <p className="text-blue-100 text-sm mb-1">활동 소장</p>
              <p className="text-2xl font-bold">{stats?.activeContractors || 0}명</p>
            </div>
          </div>
        </div>
      </div>

      {/* VIP 인출 대기 알림 - 최우선 표시 */}
      {vipWithdrawalStats?.pendingCount > 0 && (
        <div className="relative bg-gradient-to-r from-yellow-500 via-amber-600 to-orange-600 rounded-2xl p-5 text-white shadow-2xl overflow-hidden transform hover:scale-[1.01] transition-all duration-300">
          {/* 애니메이션 배경 */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-yellow-500 opacity-30 animate-pulse"></div>
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full animate-ping"></div>
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full animate-ping animation-delay-1000"></div>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-shimmer"></div>
          </div>
          
          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-4">
              <div className="relative">
                <div className="absolute inset-0 bg-white/30 rounded-full animate-ping"></div>
                <div className="relative bg-white/20 backdrop-blur p-3 rounded-full animate-bounce">
                  <BanknotesIcon className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  VIP 인출 요청 대기중!
                  <span className="text-yellow-200 animate-pulse">💰</span>
                </h3>
                <p className="text-yellow-100 text-sm">즉시 처리가 필요한 VIP 인출 요청이 있습니다</p>
              </div>
            </div>
            
            <Link 
              to="/admin/vip/withdrawals" 
              className="group flex items-center justify-between p-4 bg-white/20 backdrop-blur rounded-xl hover:bg-white/30 transition-all transform hover:-translate-y-0.5"
            >
              <div className="flex items-center gap-4">
                <div className="flex flex-col">
                  <span className="text-sm font-semibold">대기중인 인출 요청</span>
                  <span className="text-xs text-yellow-100 mt-1">총 {vipWithdrawalStats.pendingAmount?.toLocaleString()}원</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <span className="text-3xl font-bold animate-pulse">{vipWithdrawalStats.pendingCount}</span>
                  <span className="text-sm ml-1">건</span>
                </div>
                <div className="text-right">
                  <p className="text-xs text-yellow-100">실지급액</p>
                  <p className="text-lg font-bold">{vipWithdrawalStats.pendingNetAmount?.toLocaleString()}원</p>
                </div>
                <ChevronRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
            
            {vipWithdrawalStats.oldestHours > 24 && (
              <div className="mt-3 p-2 bg-red-600/30 rounded-lg border border-red-400/50">
                <p className="text-sm font-semibold flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 animate-pulse" />
                  가장 오래된 요청: {Math.floor(vipWithdrawalStats.oldestHours)}시간 경과
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 긴급 알림 섹션 - 개선된 디자인 */}
      {(stats?.urgentCount > 0 || stats?.unassignedCount > 0 || stats?.unpaidCommission > 0) && (
        <div className="relative bg-gradient-to-r from-red-500 via-red-600 to-red-700 rounded-2xl p-5 text-white shadow-2xl overflow-hidden transform hover:scale-[1.01] transition-all duration-300">
          {/* 애니메이션 배경 */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-red-600 opacity-30 animate-pulse"></div>
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full animate-ping"></div>
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full animate-ping animation-delay-1000"></div>
          </div>
          
          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-4">
              <div className="relative">
                <div className="absolute inset-0 bg-white/20 rounded-full animate-ping"></div>
                <div className="relative bg-white/20 backdrop-blur p-3 rounded-full">
                  <ExclamationTriangleIcon className="h-7 w-7 text-white" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold">긴급 상황 발생!</h3>
                <p className="text-red-100 text-sm">즉시 확인이 필요한 항목이 있습니다</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {stats?.unassignedCount > 0 && (
                <Link 
                  to="/projects?status=received&contractor_id=null" 
                  className="group flex items-center justify-between p-4 bg-white/20 backdrop-blur rounded-xl hover:bg-white/30 transition-all transform hover:-translate-y-0.5"
                >
                  <div className="flex items-center gap-3">
                    <ClockIcon className="h-5 w-5 text-white" />
                    <span className="text-sm font-semibold">30분 이상 미배정</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold">{stats.unassignedCount}</span>
                    <span className="text-xs">건</span>
                    <ChevronRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              )}
              {stats?.customerComplaint > 0 && (
                <Link 
                  to="/projects?complaint=true" 
                  className="group flex items-center justify-between p-4 bg-white/20 backdrop-blur rounded-xl hover:bg-white/30 transition-all transform hover:-translate-y-0.5"
                >
                  <div className="flex items-center gap-3">
                    <ExclamationCircleIcon className="h-5 w-5 text-white" />
                    <span className="text-sm font-semibold">고객 불만 접수</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold">{stats.customerComplaint}</span>
                    <span className="text-xs">건</span>
                    <ChevronRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              )}
              {stats?.unpaidCommission > 0 && (
                <Link 
                  to="/admin/commission-history?status=pending" 
                  className="group flex items-center justify-between p-4 bg-white/20 backdrop-blur rounded-xl hover:bg-white/30 transition-all transform hover:-translate-y-0.5"
                >
                  <div className="flex items-center gap-3">
                    <CurrencyDollarIcon className="h-5 w-5 text-white" />
                    <span className="text-sm font-semibold">수수료 입금 대기</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold">{stats.unpaidCommission}</span>
                    <span className="text-xs">건</span>
                    <ChevronRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 주요 지표 카드 - 개선된 디자인 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="group relative bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-5 border border-blue-200 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-blue-200/20 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-blue-200/50 p-3 rounded-xl group-hover:bg-blue-300/50 transition-colors">
                <DocumentTextIcon className="h-6 w-6 text-blue-700" />
              </div>
              {stats?.newTodayChange !== undefined && (
                <div className={`flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-full ${stats.newTodayChange >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {stats.newTodayChange >= 0 ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />}
                  {Math.abs(stats.newTodayChange)}%
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600 font-medium">오늘 신규 접수</p>
            <p className="text-3xl font-bold text-gray-900 mt-1 group-hover:scale-110 transition-transform origin-left">{stats?.newToday || 0}건</p>
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-gray-500">어제: {stats?.yesterdayNew || 0}건</p>
              <div className="w-16 h-1 bg-blue-200 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(100, (stats?.newToday || 0) / 10 * 100)}%` }}></div>
              </div>
            </div>
          </div>
        </div>

        <div className="group relative bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl p-5 border border-amber-200 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-amber-200/20 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-amber-200/50 p-3 rounded-xl group-hover:bg-amber-300/50 transition-colors">
                <ClockIcon className="h-6 w-6 text-amber-700" />
              </div>
              <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-1 rounded-full">
                {stats?.activeContractors || 0}명 활동
              </span>
            </div>
            <p className="text-sm text-gray-600 font-medium">계약 진행중</p>
            <p className="text-3xl font-bold text-gray-900 mt-1 group-hover:scale-110 transition-transform origin-left">{stats?.inProgress || 0}건</p>
            <div className="mt-2">
              <p className="text-xs text-gray-500">검토/계약 단계</p>
              <div className="flex gap-1 mt-1">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className={`h-1 flex-1 rounded-full ${i < Math.ceil((stats?.inProgress || 0) / 10) ? 'bg-amber-500' : 'bg-amber-200'}`}></div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="group relative bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-5 border border-emerald-200 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-200/20 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-emerald-200/50 p-3 rounded-xl group-hover:bg-emerald-300/50 transition-colors">
                <BanknotesIcon className="h-6 w-6 text-emerald-700" />
              </div>
              {stats?.todayRevenueChange !== undefined && (
                <div className={`flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-full ${stats.todayRevenueChange >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {stats.todayRevenueChange >= 0 ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />}
                  {Math.abs(stats.todayRevenueChange)}%
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600 font-medium">오늘 매출</p>
            <p className="text-2xl font-bold text-gray-900 mt-1 group-hover:scale-110 transition-transform origin-left">
              {stats?.todayRevenue ? `${(stats.todayRevenue / 10000).toLocaleString()}만원` : '0원'}
            </p>
            <div className="mt-2">
              <p className="text-xs text-gray-500 mb-1">목표 달성률</p>
              <div className="w-full bg-emerald-200 rounded-full h-2 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full transition-all duration-1000"
                  style={{ width: `${stats?.targetRate || 0}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div className="group relative bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-5 border border-purple-200 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-purple-200/20 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-purple-200/50 p-3 rounded-xl group-hover:bg-purple-300/50 transition-colors">
                <ChartBarIcon className="h-6 w-6 text-purple-700" />
              </div>
              {stats?.monthlyRevenueChange !== undefined && (
                <div className={`flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-full ${stats.monthlyRevenueChange >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {stats.monthlyRevenueChange >= 0 ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />}
                  {Math.abs(stats.monthlyRevenueChange)}%
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600 font-medium">이번달 매출</p>
            <p className="text-2xl font-bold text-gray-900 mt-1 group-hover:scale-110 transition-transform origin-left">
              {stats?.monthlyRevenue ? `${(stats.monthlyRevenue / 100000000).toFixed(1)}억` : '0원'}
            </p>
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-gray-500">전월 대비</p>
              <SparklesIcon className="h-4 w-4 text-purple-400 animate-pulse" />
            </div>
          </div>
        </div>
      </div>

      {/* 실시간 활동 & 수익 현황 - 개선된 디자인 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 실시간 활동 피드 */}
        <div className="lg:col-span-2 bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl shadow-xl overflow-hidden">
          <div className="p-6 bg-white/70 backdrop-blur border-b border-blue-100">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <div className="relative bg-blue-100 p-2 rounded-lg">
                  <BellAlertIcon className="h-5 w-5 text-blue-600" />
                  <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full animate-pulse"></span>
                </div>
                실시간 활동
              </h3>
              <Link to="/admin/activity" className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 group">
                전체보기 
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            </div>
          </div>
          <div className="p-6 space-y-3">
            {stats?.recentActivities?.length > 0 ? (
              stats.recentActivities.map((activity, index) => (
                <div 
                  key={index} 
                  onClick={() => handleActivityClick(activity)}
                  className="group flex items-start gap-4 p-4 bg-white rounded-xl hover:shadow-md transition-all transform hover:-translate-y-0.5 cursor-pointer"
                >
                  <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform ${
                    activity.type === 'project' ? 'bg-gradient-to-br from-blue-100 to-blue-200' :
                    activity.type === 'contractor' ? 'bg-gradient-to-br from-green-100 to-green-200' :
                    activity.type === 'customer' ? 'bg-gradient-to-br from-purple-100 to-purple-200' :
                    activity.type === 'message' ? 'bg-gradient-to-br from-indigo-100 to-indigo-200' :
                    activity.type === 'status' ? 'bg-gradient-to-br from-yellow-100 to-yellow-200' :
                    'bg-gradient-to-br from-amber-100 to-amber-200'
                  }`}>
                    {activity.type === 'project' && <DocumentTextIcon className="h-6 w-6 text-blue-700" />}
                    {activity.type === 'contractor' && <UserIcon className="h-6 w-6 text-green-700" />}
                    {activity.type === 'customer' && <UserGroupIcon className="h-6 w-6 text-purple-700" />}
                    {activity.type === 'message' && <ChatBubbleLeftRightIcon className="h-6 w-6 text-indigo-700" />}
                    {activity.type === 'status' && <ClockIcon className="h-6 w-6 text-yellow-700" />}
                    {activity.type === 'commission' && <CurrencyDollarIcon className="h-6 w-6 text-amber-700" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">{activity.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-gray-500">{activity.user}</p>
                      <span className="text-gray-400">•</span>
                      <p className="text-xs text-gray-500">{format(new Date(activity.created_at), 'HH:mm', { locale: ko })}</p>
                    </div>
                  </div>
                  <ChevronRightIcon className="h-4 w-4 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0" />
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <BellAlertIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">아직 활동이 없습니다</p>
              </div>
            )}
          </div>
        </div>

        {/* 수익/수수료 현황 */}
        <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl shadow-xl overflow-hidden">
          <div className="p-6 bg-white/70 backdrop-blur border-b border-emerald-100">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <div className="bg-emerald-100 p-2 rounded-lg">
                <BanknotesIcon className="h-5 w-5 text-emerald-600" />
              </div>
              수익 & 수수료
            </h3>
          </div>
          <div className="p-6 space-y-4">
            {/* 오늘 수익 */}
            <div className="relative p-4 bg-white rounded-xl border border-emerald-200 overflow-hidden group hover:shadow-md transition-all">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-50 to-teal-50 opacity-50"></div>
              <div className="relative">
                <p className="text-sm text-emerald-700 font-semibold mb-2">오늘 수익</p>
                <p className="text-2xl font-bold text-emerald-900">
                  {stats?.todayRevenue ? `${(stats.todayRevenue / 10000).toLocaleString()}만원` : '0원'}
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <div className="flex-1 bg-emerald-200 rounded-full h-2 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full transition-all duration-1000" 
                      style={{ width: `${stats?.todayTargetRate || 0}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-emerald-700 font-medium">{stats?.todayTargetRate || 0}%</span>
                </div>
              </div>
            </div>

            {/* 이번주 수익 */}
            <div className="relative p-4 bg-white rounded-xl border border-blue-200 overflow-hidden group hover:shadow-md transition-all">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-indigo-50 opacity-50"></div>
              <div className="relative">
                <p className="text-sm text-blue-700 font-semibold mb-2">이번주 수익</p>
                <p className="text-2xl font-bold text-blue-900">
                  {stats?.weeklyRevenue ? `${(stats.weeklyRevenue / 10000).toLocaleString()}만원` : '0원'}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-blue-600">전주 대비</p>
                  <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                    (stats?.weeklyChange || 0) >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {(stats?.weeklyChange || 0) >= 0 ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />}
                    {Math.abs(stats?.weeklyChange || 0)}%
                  </div>
                </div>
              </div>
            </div>

            {/* 수수료 현황 */}
            <div className="relative p-4 bg-white rounded-xl border border-purple-200 overflow-hidden group hover:shadow-md transition-all">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-50 to-pink-50 opacity-50"></div>
              <div className="relative">
                <p className="text-sm text-purple-700 font-semibold mb-3">수수료 현황</p>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600 flex items-center gap-1">
                      <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                      이번달 예상
                    </span>
                    <span className="text-sm font-bold text-purple-900">
                      {stats?.monthlyCommission ? `${(stats.monthlyCommission / 10000).toLocaleString()}만원` : '0원'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600 flex items-center gap-1">
                      <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                      미수금
                    </span>
                    <span className="text-sm font-bold text-red-600">
                      {stats?.unpaidAmount ? `${(stats.unpaidAmount / 10000).toLocaleString()}만원` : '0원'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-purple-100">
                    <span className="text-xs text-gray-600">수납률</span>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-purple-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-purple-500 rounded-full" 
                          style={{ width: `${stats?.collectionRate || 0}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-bold text-purple-900">{stats?.collectionRate || 0}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Link 
              to="/admin/commission-history" 
              className="block w-full text-center py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all text-sm font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 group"
            >
              수수료 관리 
              <span className="inline-block group-hover:translate-x-1 transition-transform">→</span>
            </Link>
          </div>
        </div>
      </div>

      {/* 프로젝트 현황 & 차트 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 프로젝트 상태별 현황 - 개선된 디자인 */}
        <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl shadow-xl overflow-hidden">
          <div className="p-6 bg-white/70 backdrop-blur border-b border-indigo-100">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <div className="bg-indigo-100 p-2 rounded-lg">
                <ChartBarIcon className="h-5 w-5 text-indigo-600" />
              </div>
              프로젝트 현황
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <Link 
              to="/projects?status=received" 
              className="group relative flex items-center justify-between p-4 bg-white rounded-xl hover:shadow-lg transition-all duration-300 border border-blue-100 hover:border-blue-200 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-indigo-50 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <DocumentTextIcon className="h-6 w-6 text-blue-700" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">접수</p>
                  <p className="text-xs text-gray-500">신규 대기</p>
                </div>
              </div>
              <div className="relative flex items-center gap-2">
                <div className="text-right">
                  <p className="text-3xl font-bold text-blue-600 group-hover:scale-110 transition-transform inline-block">{stats?.statusCount?.received || 0}</p>
                  <span className="text-sm text-gray-500 ml-1 font-medium">건</span>
                </div>
                <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-blue-600 transition-all" />
              </div>
            </Link>

            <Link 
              to="/projects?status=contract" 
              className="group relative flex items-center justify-between p-4 bg-white rounded-xl hover:shadow-lg transition-all duration-300 border border-amber-100 hover:border-amber-200 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-amber-50 to-orange-50 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-amber-200 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <ClockIcon className="h-6 w-6 text-amber-700" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">계약</p>
                  <p className="text-xs text-gray-500">진행중</p>
                </div>
              </div>
              <div className="relative flex items-center gap-2">
                <div className="text-right">
                  <p className="text-3xl font-bold text-amber-600 group-hover:scale-110 transition-transform inline-block">{stats?.statusCount?.contract || 0}</p>
                  <span className="text-sm text-gray-500 ml-1 font-medium">건</span>
                </div>
                <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-amber-600 transition-all" />
              </div>
            </Link>

            <Link 
              to="/projects?status=completed" 
              className="group relative flex items-center justify-between p-4 bg-white rounded-xl hover:shadow-lg transition-all duration-300 border border-emerald-100 hover:border-emerald-200 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-50 to-green-50 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <CheckCircleIcon className="h-6 w-6 text-emerald-700" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">완료</p>
                  <p className="text-xs text-gray-500">이번달</p>
                </div>
              </div>
              <div className="relative flex items-center gap-2">
                <div className="text-right">
                  <p className="text-3xl font-bold text-emerald-600 group-hover:scale-110 transition-transform inline-block">{stats?.statusCount?.completed || 0}</p>
                  <span className="text-sm text-gray-500 ml-1 font-medium">건</span>
                </div>
                <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-emerald-600 transition-all" />
              </div>
            </Link>
            
            {/* 전체 통계 */}
            <div className="mt-4 p-4 bg-gradient-to-r from-gray-100 to-gray-50 rounded-xl">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xs text-gray-600">총 프로젝트</p>
                  <p className="text-lg font-bold text-gray-900">
                    {(stats?.statusCount?.received || 0) + (stats?.statusCount?.contract || 0) + (stats?.statusCount?.completed || 0)}
                  </p>
                </div>
                <div className="border-x border-gray-300">
                  <p className="text-xs text-gray-600">성사율</p>
                  <p className="text-lg font-bold text-green-600">
                    {((stats?.statusCount?.contract || 0) + (stats?.statusCount?.completed || 0)) > 0 
                      ? Math.round((((stats?.statusCount?.contract || 0) + (stats?.statusCount?.completed || 0)) / 
                          ((stats?.statusCount?.received || 0) + (stats?.statusCount?.contract || 0) + (stats?.statusCount?.completed || 0))) * 100)
                      : 0}%
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">대기시간</p>
                  <p className="text-lg font-bold text-amber-600">30분</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 월별 매출 차트 - 개선된 디자인 */}
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl shadow-xl overflow-hidden">
          <div className="p-6 bg-white/70 backdrop-blur border-b border-purple-100">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <ArrowTrendingUpIcon className="h-5 w-5 text-purple-600" />
                </div>
                월별 매출 추이
              </h3>
              <span className="text-sm text-purple-600 font-medium">
                최근 6개월
              </span>
            </div>
          </div>
          <div className="p-6">
            {chartData?.length > 0 ? (
              <>
                {/* 차트 영역 */}
                <div className="h-64 flex items-end justify-between gap-3 mb-4">
                  {chartData.map((month, index) => {
                    const maxRevenue = Math.max(...chartData.map(m => m.revenue));
                    const heightPercent = maxRevenue > 0 ? (month.revenue / maxRevenue) * 100 : 0;
                    const isHighest = month.revenue === maxRevenue && month.revenue > 0;
                    
                    return (
                      <div key={index} className="flex-1 flex flex-col items-center group cursor-pointer">
                        <div className="relative w-full flex flex-col items-center">
                          {/* 툴팁 */}
                          <div className="absolute -top-10 opacity-0 group-hover:opacity-100 transition-all duration-200 z-10">
                            <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap">
                              <div className="font-bold">{(month.revenue / 10000).toLocaleString()}만원</div>
                            </div>
                            <div className="w-2 h-2 bg-gray-900 rotate-45 absolute -bottom-1 left-1/2 -translate-x-1/2"></div>
                          </div>
                          
                          {/* 바 차트 */}
                          <div 
                            className={`w-full rounded-t-xl transition-all duration-700 relative overflow-hidden ${
                              isHighest 
                                ? 'bg-gradient-to-t from-purple-600 to-purple-400 shadow-lg' 
                                : 'bg-gradient-to-t from-indigo-500 to-indigo-400'
                            } hover:shadow-xl transform hover:-translate-y-1`}
                            style={{ 
                              height: `${heightPercent * 2}px`,
                              minHeight: month.revenue > 0 ? '20px' : '0px',
                              maxHeight: '200px'
                            }}
                          >
                            {/* 애니메이션 효과 */}
                            <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity"></div>
                            {isHighest && (
                              <div className="absolute top-2 left-1/2 -translate-x-1/2">
                                <TrophyIcon className="h-4 w-4 text-white/80" />
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* 월 라벨 */}
                        <div className="mt-3 text-center">
                          <span className="text-sm font-medium text-gray-700">{month.month}월</span>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {month.revenue > 100000000 ? `${(month.revenue / 100000000).toFixed(1)}억` : `${(month.revenue / 10000).toLocaleString()}만원`}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* 통계 요약 */}
                <div className="bg-white/50 rounded-xl p-4 grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-xs text-gray-600">평균 매출</p>
                    <p className="text-lg font-bold text-gray-900">
                      {((chartData.reduce((sum, m) => sum + m.revenue, 0) / chartData.length) / 100000000).toFixed(1)}억
                    </p>
                  </div>
                  <div className="text-center border-x border-gray-200">
                    <p className="text-xs text-gray-600">최고 기록</p>
                    <p className="text-lg font-bold text-purple-600">
                      {(Math.max(...chartData.map(m => m.revenue)) / 100000000).toFixed(1)}억
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-600">성장률</p>
                    <p className="text-lg font-bold text-green-600">
                      {(() => {
                        if (chartData.length < 2) return '0%';
                        const firstRevenue = chartData[0].revenue;
                        const lastRevenue = chartData[chartData.length-1].revenue;
                        
                        // 첫 번째 달이 0이고 마지막 달도 0이면 0%
                        if (firstRevenue === 0 && lastRevenue === 0) return '0%';
                        
                        // 첫 번째 달이 0이고 마지막 달이 0이 아니면 신규
                        if (firstRevenue === 0 && lastRevenue > 0) return '신규';
                        
                        // 정상적인 성장률 계산
                        const growth = ((lastRevenue - firstRevenue) / firstRevenue) * 100;
                        return `${Math.round(growth)}%`;
                      })()}
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-64">
                <div className="animate-pulse flex space-x-3">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="w-12 bg-gray-200 rounded-t-lg" style={{ height: `${(i + 1) * 30}px` }}></div>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-4">데이터를 불러오는 중...</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 공지사항 & 도움말 & 미확인 프로젝트 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 최근 공지사항 */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-2xl shadow-xl overflow-hidden">
          <div className="p-6 bg-white/70 backdrop-blur border-b border-purple-100">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-purple-600 to-purple-700 bg-clip-text text-transparent flex items-center gap-2">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <SpeakerWaveIcon className="h-5 w-5 text-purple-600" />
                </div>
                최근 공지사항
              </h3>
              <Link to="/admin/notices" className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1 group">
                전체보기 
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            </div>
          </div>
          <div className="p-6 space-y-3">
            {recentNotices?.length > 0 ? (
              recentNotices.map((notice) => (
                <div
                  key={notice.id}
                  onClick={() => setSelectedNotice(notice)}
                  className="group relative bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-purple-200/50 hover:shadow-lg transition-all duration-300 cursor-pointer hover:scale-[1.02] hover:border-purple-300 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-50 to-pink-50 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="relative pr-8">
                    <h4 className="font-medium text-gray-800 text-sm truncate group-hover:text-purple-700 transition-colors">
                      {notice.is_important === 1 && (
                        <span className="inline-flex items-center justify-center w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></span>
                      )}
                      {notice.title}
                    </h4>
                    <p className="text-xs text-purple-600 mt-1">
                      {format(new Date(notice.created_at), 'MM월 dd일 HH:mm')}
                    </p>
                  </div>
                  <ChevronRightIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-all" />
                </div>
              ))
            ) : (
              <div className="text-center py-12 bg-white/50 rounded-xl">
                <SpeakerWaveIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm text-gray-500">등록된 공지사항이 없습니다</p>
              </div>
            )}
          </div>
        </div>

        {/* 최근 도움말 */}
        <div className="bg-gradient-to-br from-teal-50 to-teal-100/50 rounded-2xl shadow-xl overflow-hidden">
          <div className="p-6 bg-white/70 backdrop-blur border-b border-teal-100">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-teal-600 to-teal-700 bg-clip-text text-transparent flex items-center gap-2">
                <div className="bg-teal-100 p-2 rounded-lg">
                  <QuestionMarkCircleIcon className="h-5 w-5 text-teal-600" />
                </div>
                최근 도움말
              </h3>
              <Link to="/admin/helps" className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 group">
                전체보기 
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            </div>
          </div>
          <div className="p-6 space-y-3">
            {recentHelps?.length > 0 ? (
              recentHelps.map((help) => (
                <div 
                  key={help.id} 
                  onClick={() => setSelectedHelp(help)}
                  className="group relative block bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-teal-200/50 hover:shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-teal-300 overflow-hidden cursor-pointer"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-teal-50 to-cyan-50 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="relative pr-8">
                    <h4 className="font-medium text-gray-800 text-sm truncate group-hover:text-teal-700 transition-colors">{help.title}</h4>
                    <p className="text-xs text-teal-600 mt-1">
                      {help.category} · {format(new Date(help.created_at), 'MM월 dd일')}
                    </p>
                  </div>
                  <ChevronRightIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-teal-600 group-hover:translate-x-1 transition-all" />
                </div>
              ))
            ) : (
              <div className="text-center py-12 bg-white/50 rounded-xl">
                <QuestionMarkCircleIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm text-gray-500">등록된 도움말이 없습니다</p>
              </div>
            )}
          </div>
        </div>

        {/* 미확인 프로젝트 */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl shadow-xl overflow-hidden">
          <div className="p-6 bg-white/70 backdrop-blur border-b border-amber-100">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent flex items-center gap-2">
                <div className="bg-amber-100 p-2 rounded-lg">
                  <ClockIcon className="h-5 w-5 text-amber-600" />
                </div>
                소장 미확인 프로젝트
              </h3>
              <Link to="/admin/fcm-test" className="text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1 group">
                전체보기 
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            </div>
          </div>
          <div className="p-6 space-y-3">
            {unreadProjects?.length > 0 ? (
              unreadProjects.slice(0, 3).map((project) => (
                <Link 
                  key={project.id} 
                  to={`/projects/${project.id}`} 
                  className="group relative block bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-amber-200/50 hover:shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-amber-300 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-50 to-orange-50 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="relative flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-800 text-sm truncate group-hover:text-amber-700 transition-colors">{project.title}</h4>
                      <p className="text-xs text-amber-600 mt-1">
                        {project.contractor_name} · {project.unread_hours}시간 전 배정
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 animate-pulse" />
                      </div>
                      <ChevronRightIcon className="h-4 w-4 text-gray-400 group-hover:text-amber-600 group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="text-center py-12 bg-white/50 rounded-xl">
                <CheckCircleIcon className="h-16 w-16 mx-auto mb-3 text-green-500" />
                <p className="text-gray-600 font-medium">모든 프로젝트가 확인되었습니다!</p>
                <p className="text-sm text-gray-500 mt-1">새로운 프로젝트가 배정되면 여기에 표시됩니다</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 빠른 액션 - 개선된 디자인 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link to="/admin/projects" className="group relative overflow-hidden bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 text-white rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
          {/* 배경 장식 */}
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
          <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full -ml-8 -mb-8"></div>
          
          <div className="relative z-10">
            <div className="bg-white/20 backdrop-blur w-14 h-14 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <DocumentTextIcon className="h-8 w-8" />
            </div>
            <p className="font-bold text-lg">프로젝트 관리</p>
            <p className="text-xs text-blue-100 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">프로젝트 현황 →</p>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
        </Link>
        
        <Link to="/admin/contractors" className="group relative overflow-hidden bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 text-white rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
          {/* 배경 장식 */}
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
          <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full -ml-8 -mb-8"></div>
          
          <div className="relative z-10">
            <div className="bg-white/20 backdrop-blur w-14 h-14 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <UserGroupIcon className="h-8 w-8" />
            </div>
            <p className="font-bold text-lg">소장 관리</p>
            <p className="text-xs text-emerald-100 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">소장 정보 →</p>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
        </Link>
        
        <Link to="/admin/commission-history" className="group relative overflow-hidden bg-gradient-to-br from-purple-500 via-purple-600 to-pink-700 text-white rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
          {/* 배경 장식 */}
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
          <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full -ml-8 -mb-8"></div>
          
          <div className="relative z-10">
            <div className="bg-white/20 backdrop-blur w-14 h-14 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <CurrencyDollarIcon className="h-8 w-8" />
            </div>
            <p className="font-bold text-lg">수수료내역</p>
            <p className="text-xs text-purple-100 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">정산 관리 →</p>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
        </Link>
        
        <Link to="/admin/analytics" className="group relative overflow-hidden bg-gradient-to-br from-amber-500 via-amber-600 to-orange-700 text-white rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
          {/* 배경 장식 */}
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
          <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full -ml-8 -mb-8"></div>
          
          <div className="relative z-10">
            <div className="bg-white/20 backdrop-blur w-14 h-14 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <ChartBarIcon className="h-8 w-8" />
            </div>
            <p className="font-bold text-lg">통계 분석</p>
            <p className="text-xs text-amber-100 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">리포트 →</p>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
        </Link>
      </div>
      {/* 공지사항 모달 */}
      {selectedNotice && (
        <NoticeModal
          notice={selectedNotice}
          onClose={() => setSelectedNotice(null)}
        />
      )}
      
      {/* 도움말 모달 */}
      {selectedHelp && (
        <HelpModal
          help={selectedHelp}
          onClose={() => setSelectedHelp(null)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;